import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";

const Privacy = () => {
  return (
    <PageLayout
      seo={{
        title: "Privacy Policy - Eunice",
        description: "How Eunice collects and protects your data. GDPR compliant.",
        canonical: "/privacy",
      }}
    >
      <article className="opacity-0 animate-fade-up">
        <header className="mb-12">
          <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground font-sans">
            Last updated: January 24, 2025
          </p>
        </header>

        <div className="space-y-10 text-charcoal-light leading-relaxed font-sans">
          <section>
            <p className="text-lg">
              At Eunice, we believe in treating your information the way we'd want ours treated—with respect, care, and transparency. This policy explains what we collect, why we need it, and how we protect it.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">What We Collect</h2>
            <p className="mb-4">When you request a letter from Eunice, we collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Your name</strong> — so Eunice can address you personally</li>
              <li><strong>Your email address</strong> — so we can send your letter</li>
              <li><strong>Your destination city</strong> — so Eunice knows what to write about</li>
              <li><strong>Your travel preferences</strong> — to personalize your letter (optional)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Why We Collect It</h2>
            <p>
              Simply put: to create and deliver your personalized travel letter. Nothing more. We don't sell your data, we don't use it for advertising, and we don't share it with anyone who doesn't need it to get your letter to you.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">How We Use Your Data</h2>
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Process your payment ($1.99 per letter)</li>
              <li>Generate your personalized letter using AI</li>
              <li>Send the finished letter to your email</li>
              <li>Keep a record of your order (so you can request re-delivery if needed)</li>
            </ol>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Third-Party Services</h2>
            <p className="mb-4">We work with trusted partners to deliver your letter:</p>
            <ul className="list-disc list-inside space-y-3 ml-2">
              <li>
                <strong>Stripe</strong> — processes your payment securely. We never see or store your card details.{" "}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Stripe's Privacy Policy
                </a>
              </li>
              <li>
                <strong>Anthropic</strong> — powers Eunice's letter-writing (Claude AI). Your city and preferences are used to generate your letter.{" "}
                <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Anthropic's Privacy Policy
                </a>
              </li>
              <li>
                <strong>Resend</strong> — delivers your letter by email.{" "}
                <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Resend's Privacy Policy
                </a>
              </li>
              <li>
                <strong>Supabase</strong> — stores your order information securely.{" "}
                <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Supabase's Privacy Policy
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Data Retention</h2>
            <p>
              We keep your order information indefinitely so you can request re-delivery of your letter if needed. However, you can request deletion of your data at any time—just email us and we'll remove everything within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Your Rights</h2>
            <p className="mb-4">Under GDPR and similar privacy laws, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Access</strong> — request a copy of all data we have about you</li>
              <li><strong>Correction</strong> — fix any inaccuracies in your data</li>
              <li><strong>Deletion</strong> — request we delete your data entirely</li>
              <li><strong>Portability</strong> — receive your data in a standard format</li>
              <li><strong>Objection</strong> — object to how we process your data</li>
              <li><strong>Withdraw consent</strong> — change your mind at any time</li>
            </ul>
            <p className="mt-4">
              These rights apply whether you're in the EU or anywhere else. We believe everyone deserves privacy.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Cookies</h2>
            <p>
              We use minimal, essential cookies to keep the site working properly. We don't use tracking cookies or analytics that follow you around the internet.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-foreground mb-4">Contact Us</h2>
            <p>Questions? Concerns? Want to exercise any of your rights? We're here to help.</p>
            <p className="mt-4">
              <strong>Email:</strong>{" "}
              <a href="mailto:letters@eunice.world" className="text-primary hover:underline">
                letters@eunice.world
              </a>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">We'll respond within 30 days.</p>
          </section>

          <section className="pt-6 border-t border-border/30">
            <p className="text-sm text-muted-foreground">
              See also our{" "}
              <Link to="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>
              {" "}for information about how our service works.
            </p>
          </section>
        </div>
      </article>
    </PageLayout>
  );
};

export default Privacy;
